import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';

const DashboardLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [pageTitle, setPageTitle] = useState('Dashboard');
  const location = useLocation();

  // Check mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Update page title based on route
  useEffect(() => {
    const path = location.pathname;
    
    // Route to title mapping
    const routeTitles = {
      '/dashboard': 'Dashboard',
      
      // CRM
      '/dashboard/crm/leads': 'Leads',
      '/dashboard/crm/contacts': 'Contacts',
      '/dashboard/crm/accounts': 'Accounts',
      '/dashboard/crm/opportunities': 'Opportunities',
      '/dashboard/crm/deals': 'Deals',
      '/dashboard/crm/campaigns': 'Campaigns',
      
      // Enquiry
      '/dashboard/enquiry/all': 'All Enquiries',
      '/dashboard/enquiry/pending': 'Pending Enquiries',
      '/dashboard/enquiry/followup': 'Follow-up Enquiries',
      '/dashboard/enquiry/converted': 'Converted Enquiries',
      
      // HRMS
      '/dashboard/hrms/employees': 'Employees',
      '/dashboard/hrms/attendance': 'Attendance',
      '/dashboard/hrms/leave': 'Leave Management',
      '/dashboard/hrms/payroll': 'Payroll',
      '/dashboard/hrms/recruitment': 'Recruitment',
      '/dashboard/hrms/performance': 'Performance',
      '/dashboard/hrms/training': 'Training',
      
      // Projects
      '/dashboard/projects/all': 'Projects',
      '/dashboard/projects/tasks': 'Tasks',
      '/dashboard/projects/milestones': 'Milestones',
      '/dashboard/projects/gantt': 'Gantt Chart',
      '/dashboard/projects/timetracking': 'Time Tracking',
      '/dashboard/projects/sprints': 'Sprints',
      '/dashboard/projects/bugs': 'Bug Tracking',
      
      // MDM
      '/dashboard/mdm/products': 'Product Master',
      '/dashboard/mdm/customers': 'Customer Master',
      '/dashboard/mdm/vendors': 'Vendor Master',
      '/dashboard/mdm/locations': 'Location Master',
      '/dashboard/mdm/categories': 'Category Master',
      
      // Inventory
      '/dashboard/inventory/stock': 'Stock Management',
      '/dashboard/inventory/purchase-orders': 'Purchase Orders',
      '/dashboard/inventory/transfer': 'Stock Transfer',
      '/dashboard/inventory/adjustment': 'Stock Adjustment',
      
      // Sales
      '/dashboard/sales/quotations': 'Quotations',
      '/dashboard/sales/orders': 'Sales Orders',
      '/dashboard/sales/invoices': 'Invoices',
      '/dashboard/sales/payments': 'Payments',
      
      // Reports
      '/dashboard/reports/sales': 'Sales Reports',
      '/dashboard/reports/hr': 'HR Reports',
      '/dashboard/reports/projects': 'Project Reports',
      '/dashboard/reports/financial': 'Financial Reports',
      '/dashboard/reports/custom': 'Custom Reports',
      
      // Communication
      '/dashboard/communication/messages': 'Messages',
      '/dashboard/communication/announcements': 'Announcements',
      '/dashboard/communication/notifications': 'Notifications',
      
      // Settings
      '/dashboard/settings/company': 'Company Settings',
      '/dashboard/settings/users': 'User Management',
      '/dashboard/settings/roles': 'Roles & Permissions',
      '/dashboard/settings/system': 'System Settings',
      '/dashboard/settings/integrations': 'Integrations',
      
      // Help
      '/dashboard/help': 'Help & Support',
    };

    // Set page title based on current path
    const title = routeTitles[path] || 'Dashboard';
    setPageTitle(title);
    
    // Also update document title
    document.title = `${title} - Tracklio`;
  }, [location]);

  const styles = {
    layout: {
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
    },
    mainContent: {
      flex: 1,
      marginLeft: isSidebarOpen && !isMobile ? '280px' : '0',
      transition: 'margin-left 0.3s ease',
      minHeight: '100vh',
    },
    contentArea: {
      padding: isMobile ? '20px 15px' : '30px 40px',
      marginTop: '64px',
      maxWidth: '100%',
      overflowX: 'hidden',
    },
    topBar: {
      position: 'fixed',
      top: 0,
      left: isSidebarOpen && !isMobile ? '280px' : '0',
      right: 0,
      height: '64px',
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 30px',
      zIndex: 996,
      transition: 'left 0.3s ease',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    pageTitle: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0,
    },
    toggleButton: {
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      border: 'none',
      padding: '8px',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
  };

  return (
    <div style={styles.layout}>
      <Sidebar 
        isOpen={isSidebarOpen} 
        onClose={() => setIsSidebarOpen(false)}
        onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      
      <div style={styles.mainContent}>
        <div style={styles.topBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {isMobile && (
              <button
                style={styles.toggleButton}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#1e293b">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            )}
            <h1 style={styles.pageTitle}>{pageTitle}</h1>
          </div>
          
          <div style={{ fontSize: '14px', color: '#64748b' }}>
            {new Date().toLocaleDateString('en-IN', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </div>
        </div>
        
        <div style={styles.contentArea}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;